<template>
  <ValidationObserver
    ref="form"
    v-slot="{ invalid, errors }"
    class="formbody formbody--narrow formbody--center"
    tag="form"
    @submit.prevent="onSubmit"
    novalidate
  >
    <GeneralMessage
      v-if="formResponse.message"
      ref="message"
      :message="formResponse.message"
      :type="formResponse.type"
      @close="formResponse.message = ''"
    />

    <div
      v-if="fieldSections.length > 1"
      ref="progress"
      class="form-progress"
    >
      <progress
        class="form-progress__bar"
        :max="fieldSections.length"
        :value="currentSection + 1"
      />
    </div>

    <template v-for="(group, i) in fieldSections">
      <section
        v-if="currentSection >= i"
        :key="i"
        ref="section"
        class="form-section"
      >
        <h2
          v-if="group.title"
          class="form-section__title"
          v-sanitize="group.title"
        />
        <ValidationProvider
          v-for="(field, i) in group.fields"
          :key="i"
          class="input-wrapper"
          :name="field.field.name"
          :rules="field.rules"
          v-slot="{ errors, invalid, validated, dirty }"
          tag="div"
          :customMessages="field.errors"
        >
          <div
            v-if="field.attributes.html"
            class="form-section__text"
            v-sanitize="field.attributes.html"
          />
          <template v-else>
            <component
              :is="field.component"
              :type="field.type"
              v-model="formData[field.field.name]"
              v-bind="field.attributes"
              :error="invalid && validated"
              :disabled="loading"
            />
            <span
              class="input-error"
              v-if="invalid && validated"
              v-html="errors[0]"
            />
          </template>
        </ValidationProvider>
      </section>
      <Button
        v-if="fieldSections.length && currentSection === i && currentSection < fieldSections.length - 1"
        level="primary"
        :text="$t('forms.buttons.next')"
        type="button"
        @click.stop.prevent="onNextSection(i)"
      />
    </template>

    <Button
      v-if="currentSection === fieldSections.length - 1""
      level="primary"
      :text="buttonText"
      type="submit"
    />
  </ValidationObserver>
</template>

<script>
import GenericMixin from '@/mixins/GenericVueMixin';
import { mapGetters } from "vuex"
import scrrroll from 'scrrroll'
import { ValidationProvider, ValidationObserver } from "vee-validate"
import FormInput from "@/components/FormInput"
import FormInputCheckboxes from "@/components/FormInputCheckboxes"
import FormInputDate from "@/components/FormInputDate"
import FormInputRadio from "@/components/FormInputRadio"
import FormInputRadioRating from "@/components/FormInputRadioRating"
import FormInputRange from "@/components/FormInputRange"
import FormInputSelect from "@/components/FormInputSelectCustom"
import FormInputTextarea from "@/components/FormInputTextarea"
import Button from '@/components/Button'
import GeneralMessage from '@/components/GeneralMessage'
import { contaoService } from '@/service/index'

export default {
	mixins: [
		GenericMixin,
	],
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInput,
		FormInputCheckboxes,
    FormInputDate,
    FormInputRadio,
    FormInputRadioRating,
    FormInputRange,
    FormInputSelect,
    FormInputTextarea,
		Button,
		GeneralMessage,
	},
	props: {
	},
	data() {
		return {
      couponsLeft: this.$root.options.couponsLeft,
      contaoContentId: this.$root.options.contaoContentId,
      form: this.$root.options.form,
			formData: {},
      buttonText: '',
      currentSection: 0,

			formResponse: {
				type: 'info',
				message: '',
			},
      supportedTypes: [
        'explanation',
        'html',
        'select',
        'text',
        'alpha',
        'extnd',
        'email',
        'phone',
        'httpurl',
        'url',
        'digit',
        'date',
        'password',
        'fieldsetStart',
        'fieldsetStop',
        'textarea',
        'radio',
        'checkbox',
        'range',
        'hidden',
      ],
		}
	},
	computed: {
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
    fieldsEnhanced() {
      return this.form?.fields
        ?.filter(field => this.supportedTypes.includes(field.type))
        ?.map(field => {
          const attributes = {
            name: field.name,
            label: field.label,
            labelOption: !field.mandatory ? this.$t('forms.labels.optional') : undefined,
            placeholder: field.placeholder || field.label,
            legend: field.label,
            legendOption: !field.mandatory ? this.$t('forms.labels.optional') : undefined,
            minlength: field.minlength || undefined,
            maxlength: field.maxlength || undefined,
            min: field.min || undefined,
            max: field.max || undefined,
            step: field.step || undefined,
            required: field.mandatory,
            html: field.html || field.text || undefined,
            vertical: true,
            noFloat: true,
          }
          let customType = null

          if (field.options?.length) {
            attributes.options = field.options
          }

          if (field.customTemplate === 'form_radio_rating') {
            customType = 'radiorating'
          }

          const type = customType ?? (this.fieldTypeMap(field.validation) ? field.validation : field.type)

          return {
            field,
            ...(this.fieldTypeMap(type) ?? {}),
            attributes: Object.entries(attributes)
              .filter(([key]) => this.fieldTypeAllowedAttributes(type)?.includes?.(key))
              .reduce((o, [key, value]) => {
                o[key] = value
                return o
              }, {}),
            ...this.fieldValidators(field)
          }
        }) || []
    },
    fieldSections() {
      return this.fieldsEnhanced.reduce((a, f, i, ar) => {
        const prevField = ar[i - 1]
        if (f.field.type === 'fieldsetStart') {
          a.push({
            title: f.field.label,
            fields: []
          })
        }
        // else if (f.field.type === 'fieldsetStop') {
        //   a.push({
        //     fields: []
        //   })
        // }
        else if (!a.length || prevField?.field.type === 'fieldsetStop') {
          a.push({
            fields: [f]
          })
        }
        else {
          a[a.length - 1].fields.push(f)
        }

        return a
      }, [])
    }
	},
	methods: {
    fieldTypeMap(fieldType) {
      let component = undefined
      let type = undefined

      switch(fieldType) {
        case 'explanation':
        case 'html':
          component = 'div';
          break;
        case 'text':
        case 'alpha':
        case 'xtnd':
        case 'custom':
          component = FormInput
          type = 'text'
          break;
        case 'digit':
          component = FormInput
          type = 'number'
          break;
        case 'email':
          component = FormInput
          type = 'email'
          break;
        case 'phone':
          component = FormInput
          type = 'tel'
          break;
        case 'date':
          component = FormInputDate
          break;
        case 'httpurl':
        case 'url':
          component = FormInput
          type = 'url'
          break;
        case 'password':
          component = FormInput
          type = 'password'
          break;
        case 'textarea':
          component = FormInputTextarea
          break;
        case 'select':
          component = FormInputSelect
          break;
        case 'radio':
          component = FormInputRadio
          break;
        case 'radiorating':
          component = FormInputRadioRating
          break;
        case 'checkbox':
          component = FormInputCheckboxes
          break;
        case 'range':
          component = FormInputRange
          break;
        case 'hidden':
          component = 'input'
          type = 'hidden'
          break;
      }

      if (!component && !type) {
        return null
      }

      else if (!type) {
        return {
          component
        }
      }

      return {
        component,
        type,
      }
    },
    fieldTypeAllowedAttributes(fieldType) {
      let attributes = []
      switch(fieldType) {
        case 'explanation':
        case 'html':
          attributes = [
            'html'
          ]
          break;
        case 'text':
        case 'email':
        case 'alpha':
        case 'extnd':
        case 'phone':
        case 'httpurl':
        case 'url':
        case 'custom':
        case 'password':
          attributes = [
            'name',
            'label',
            'labelOption',
            'placeholder',
            'minlength',
            'maxlength',
            'required',
          ]
          break;
        case 'textarea':
          attributes = [
            'name',
            'label',
            'labelOption',
            'noFloat',
            'placeholder',
            'minlength',
            'maxlength',
            'required',
          ]
          break;
        case 'digit':
          attributes = [
            'name',
            'label',
            'labelOption',
            'placeholder',
            'min',
            'max',
            'step',
            'required',
          ]
          break;
        case 'date':
          attributes = [
            'name',
            'label',
            'labelOption',
            'placeholder',
            'required',
          ]
          break;
        case 'select':
          attributes = [
            'name',
            'label',
            'labelOption',
            'placeholder',
            'required',
            'options',
          ]
          break;
        case 'radio':
        case 'radiorating':
        case 'checkbox':
          attributes = [
            'name',
            'legend',
            'legendOption',
            'required',
            'options',
            'vertical',
          ]
          break;
        case 'range':
          attributes = [
            'name',
            'min',
            'max',
            'step',
            'required',
          ]
          break;
        case 'hidden':
          attributes = [
            'name',
            'required',
          ]
          break;
      }

      return attributes
    },
    fieldValidators(field) {
      const rules = {
        required: field.mandatory,
      }
      const errors = {
        required: this.$t('forms.errors.fieldEmpty')
      }

      // TODO: add validation for min-/maxlength and min-/maxvalue

      if (field.validation) {
        switch (field.validation) {
          case 'email':
            rules.email = true
            errors.email = field.error || this.$t('forms.errors.emailInvalid')
            break;
          case 'digit':
            rules.numeric = true
            errors.numeric = field.error || this.$t('forms.errors.format')
            break;
          case 'alpha':
            rules.alpha_num = true
            errors.alpah_num = field.error || this.$t('forms.errors.format')
            break;
          case 'extnd':
            rules.alpha_num = true
            errors.alpha_num = field.error || this.$t('forms.errors.format')
            break;
          case 'phone':
            rules.phone = true
            errors.phone = field.error || this.$t('forms.errors.phone')
            break;
        }

        if (field.error) {
          let regex = field.validation
          if (regex.startsWith('/')) {
            regex = regex.substring(1)
          }
          if (regex.endsWith('/')) {
            regex = regex.substring(0, regex.length - 1)
          }
          rules.regex = new RegExp(regex)
          errors.regex = field.error || this.$t('forms.errors.format')
        }
      }

      return {
        rules,
        errors
      }
    },
    async onNextSection(index) {
			this.loading = true;
			const valid = await this.$refs.form.validate();
      if (valid) {
        this.currentSection = Math.min(index + 1, this.fieldSections.length - 1)
        await this.$nextTick()
        scrrroll.to(this.$refs.section[this.currentSection])
      } else {
				await this.$nextTick();
				const inputError = this.$refs.form.$el.querySelector('.input-error');

				if (inputError) {
					scrrroll.to(inputError.parentNode)
				}
			}
      this.loading = false
    },
		async onSubmit(e) {
			e.preventDefault();
			this.loading = true;

			const valid = await this.$refs.form.validate();

			if (valid) {
				try {
					const response = await contaoService.sendForm({
						id: this.form.id,
            data: this.formData
					});

          if (this.form.redirect) {
            window.location.href = this.form.redirect
          }
				}
				catch(err) {
          this.isDebug() && console.error(err.response)

          if (err?.response?.data?.errors) {
            this.$refs.form.setErrors(err.response.data.errors)

            await this.$nextTick();
            const inputError = this.$refs.form.$el.querySelector('.input-error');

            if (inputError) {
              scrrroll.to(inputError.parentNode)
            }
          } else if (err?.response?.data?.message) {
						this.formResponse.message = err.response.data.message
					} else {
						this.formResponse.message = this.$t('forms.errors.generic');
					}
					if (this.formResponse.message) {
            this.formResponse.type = 'error';
            this.$nextTick(() => {
              this.$refs.message.scrollTo();
            });
          }
					this.loading = false;
				}
			} else {
				await this.$nextTick();
				const inputError = this.$refs.form.$el.querySelector('.input-error');

				if (inputError) {
					scrrroll.to(inputError.parentNode)
				}
				this.loading = false
			}
		},
    updateScrollOffset() {
      scrrroll.offset = this.$refs?.progress?.clientHeight || 0
    }
	},
	mounted() {
    const button = this.form?.fields?.find(f => f.type === 'submit');
    this.buttonText = button?.label || this.$t('forms.buttons.send')
		this.form?.fields?.forEach(field => {
      if (field.name) {
        let value = field.default
        if (field.type === 'checkbox') {
          value = field.options?.reduce((a, o) => {
            if (o.default) {
              a.push(o.value)
            }
            return a
          }, []) || []
        }
        else if (field.type === 'radio') {
          value = field.options?.find(o => o.default)?.value || value
        }
        this.formData[field.name] = value
      }
		})
    this.formData.contaoContentId = this.contaoContentId
    window.addEventListener('resize', this.updateScrollOffset)
    this.updateScrollOffset()
	},
  destroyed() {
    window.removeEventListener('resize', this.updateScrollOffset)
  }
}
</script>
